import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchMovies } from "@/utils/movie";

interface SearchBoxProps {
  variant?: "header" | "page";
  initialValue?: string;
  placeholder?: string;
}

export default function SearchBox({
  variant = "header",
  initialValue = "",
  placeholder = "搜索“激情丛林”或影视名称",
}: SearchBoxProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const suggestions = useMemo(() => {
    if (!value.trim()) return [];
    return searchMovies(value).slice(0, 6);
  }, [value]);

  const goSearch = () => {
    const keyword = value.trim();
    setOpen(false);
    if (!keyword) return;
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    goSearch();
  };

  const isPage = variant === "page";

  return (
    <div ref={containerRef} className="relative w-full">
      <form onSubmit={handleSubmit} className="w-full">
        <div
          className={
            isPage
              ? "flex items-center gap-2 border border-background-300 bg-background-50 rounded-full pl-4 pr-1.5 py-1.5 focus-within:border-primary-400 transition-colors"
              : "flex items-center gap-2 border border-background-300 bg-background-100 rounded-full pl-3.5 pr-1 py-1 focus-within:border-primary-400 transition-colors"
          }
        >
          <i className="ri-search-line text-foreground-400 text-base" aria-hidden="true"></i>
          <input
            type="text"
            name="keyword"
            value={value}
            placeholder={placeholder}
            onChange={(event) => {
              setValue(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className="flex-1 min-w-0 bg-transparent outline-none text-sm text-foreground-900 placeholder:text-foreground-400 w-full"
            aria-label="搜索影视内容"
          />
          <button
            type="submit"
            className={
              isPage
                ? "shrink-0 flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer transition-colors"
                : "shrink-0 flex items-center justify-center w-8 h-8 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full cursor-pointer transition-colors"
            }
          >
            {isPage ? (
              <>
                <i className="ri-search-line text-base" aria-hidden="true"></i>
                搜索
              </>
            ) : (
              <i className="ri-search-line text-base" aria-hidden="true"></i>
            )}
          </button>
        </div>
      </form>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-background-50 border border-background-200 rounded-lg overflow-hidden z-50 animate-fade-in">
          <p className="px-4 pt-3 pb-1 text-xs text-foreground-400">相关内容</p>
          <ul className="pb-1.5">
            {suggestions.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/detail/${item.id}`);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-primary-50 cursor-pointer transition-colors"
                >
                  <span className="w-8 h-11 rounded-md overflow-hidden shrink-0 bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title}封面`}
                      title={`${item.title} 激情丛林 影视`}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-foreground-900 truncate">{item.title}</span>
                    <span className="block text-xs text-foreground-400">
                      {item.year} · {item.type}
                    </span>
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-300 text-lg" aria-hidden="true"></i>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}